/*
 * Copyright (c) Vaisala Oyj. All rights reserved.
 */

import * as React from 'react';
import { isMobileOnly } from 'react-device-detect';
import { Heading, Size, Tooltip, Placement, Flex, Button, ButtonType, Icon, RadioGroup, RadioButton, Checkbox } from '@vaisala-rockhopper/components';
import ToggleButton from '../inputs/ToggleButton';
import selectOptions from '../products/selectOptions.json';
import './SelectSettingsBlock.scss';
import { VaiIcon } from '@vaisala-rockhopper/design-tokens';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import IconButton from '../navigation/IconButton';

interface SelectSettingsBlockProps {
  onResetConfiguration: () => void;
  onMeasurementSelectChange: (selectParam: string, paramValue: boolean) => void;
  selectedMeasurements: string[];
  transmitterType: string;
  setTransmitterType: (value: string) => void;
}

const SelectSettingsBlock = ({ onMeasurementSelectChange, ...props }: SelectSettingsBlockProps) => {
  const { t } = useTranslation();

  const onChangeTransmitterType = (event: React.ChangeEvent<HTMLInputElement>) => {
    props.setTransmitterType(event.target.value);
  };

  const [transmitterType, setTransmitterType] = React.useState(props.transmitterType ?? '');

  React.useEffect(() => {
    if (props.transmitterType) {
      setTransmitterType(props.transmitterType)
    }
  }, [props.transmitterType])

  const [selectParameterOptions, setSelectParameterOptions] = React.useState(selectOptions.measurements)

  React.useEffect(() => {
    if (transmitterType === "handheld") {
      const filteredSelectParameterOptions = selectParameterOptions.filter(option => option.value !== "barometric_pressure");
      setSelectParameterOptions(filteredSelectParameterOptions)
    } else {
      setSelectParameterOptions(selectOptions.measurements)
    }
  }, [transmitterType])

  return (
    <div className={`measurement-block ${isMobileOnly ? 'container--mobile' : 'container--desktop'}`}>
      <Heading level={2} className="vai-margin-top-none">1. {t('ui.measure.heading')}</Heading>
      <Heading level={3}>
        {t('ui.transmitter')}
        <Tooltip
          arrow={true}
          placement={Placement.BottomStart}
          triggerElement={
            <Button buttonType={ButtonType.Link} className="info-button tooltip" buttonSize={Size.M} startIcon={<Icon name={VaiIcon.HelpO} />} />
          }
        >
          {t('ui.tooltip.transmitter')}
        </Tooltip>
      </Heading>
      <RadioGroup onChange={onChangeTransmitterType} value={transmitterType}>
        {['handheld', 'fixed'].map((key) => (
          <RadioButton key={key} value={key}>
            {t('ui.'+key)}
          </RadioButton>
        ))}
      </RadioGroup>
      <Heading level={3}>
        {t('ui.measure_parameters')}
        <Tooltip
          arrow={true}
          placement={Placement.BottomStart}
          triggerElement={
            <Button buttonType={ButtonType.Link} className="info-button tooltip" buttonSize={Size.M} startIcon={<Icon name={VaiIcon.HelpO} />} />
          }
        >
          {t('ui.tooltip.parameters')}
        </Tooltip>
      </Heading>
      <Flex alignContent="stretch" justifyContent="space-between" flexDirection="column" flexWrap="nowrap" alignItems="stretch">
        {selectParameterOptions.map((val, i) => {
          return (
            <Checkbox
              key={val.value}
              checked={props.selectedMeasurements.includes(val.value)}
              label={val.label[i18next.language as keyof typeof val.label]}
              onChange={() => onMeasurementSelectChange(val.value, !props.selectedMeasurements.includes(val.value))}
            />
            // <ToggleButton
            //   key={val.value}
            //   selectindex={i}
            //   value={val.value}
            //   variant="dark"
            //   buttonSize={Size.M}
            //   onMeasurementSelectChange={onMeasurementSelectChange}
            //   isactive={props.selectedMeasurements.includes(val.value)}
            // >
            //   {val.label[i18next.language as keyof typeof val.label]}
            // </ToggleButton>
          );
        })}
        <Button
          onClick={() => props.onResetConfiguration()}
          buttonType={ButtonType.Link}
          color={'aqua-medium'}
          startIcon={<Icon name={VaiIcon.Refresh} />}
          className="reset-button"
        >
          {t('ui.reset')}
        </Button>
      </Flex>
    </div>
  );
};

export default SelectSettingsBlock;
